(() => {
  const elementName = "glamora-campaign-countdown";

  if (customElements.get(elementName)) {
    return;
  }

  class GlamoraCampaignCountdown extends HTMLElement {
    static parseTimestamp(value) {
      if (!value) {
        return null;
      }

      const timestamp = Date.parse(value);
      return Number.isFinite(timestamp) ? timestamp : null;
    }

    static resolvePhase(now, end) {
      if (![now, end].every(Number.isFinite)) {
        return "invalid";
      }

      if (now < end) {
        return "live";
      }

      return "expired";
    }

    connectedCallback() {
      this.cleanup();
      this.endTime = GlamoraCampaignCountdown.parseTimestamp(
        this.dataset.campaignEnd,
      );
      this.timeOffset = this.getPreviewOffset();
      this.state = null;
      this.visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          this.update();
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);

      if (!this.update()) {
        return;
      }

      this.intervalId = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      this.cleanup();
    }

    getPreviewOffset() {
      const designMode =
        this.dataset.designMode === "true" && Boolean(window.Shopify?.designMode);
      const previewMode = designMode
        ? this.dataset.previewMode || "automatic"
        : "automatic";

      if (previewMode === "before_end" && Number.isFinite(this.endTime)) {
        return this.endTime - 10 * 1000 - Date.now();
      }

      if (previewMode === "expired" && Number.isFinite(this.endTime)) {
        return this.endTime - Date.now();
      }

      return 0;
    }

    getCurrentTime() {
      return Date.now() + this.timeOffset;
    }

    update() {
      const now = this.getCurrentTime();
      const nextState = GlamoraCampaignCountdown.resolvePhase(
        now,
        this.endTime,
      );

      if (nextState === "invalid" || nextState === "expired") {
        this.expire(nextState);
        return false;
      }

      if (this.state !== "live") {
        this.activate();
      }

      const remaining = this.endTime - now;

      if (remaining <= 0) {
        this.expire("expired");
        return false;
      }

      const second = 1000;
      const minute = second * 60;
      const hour = minute * 60;
      const day = hour * 24;
      const values = {
        days: Math.floor(remaining / day),
        hours: Math.floor((remaining % day) / hour),
        minutes: Math.floor((remaining % hour) / minute),
        seconds: Math.floor((remaining % minute) / second),
      };

      this.querySelectorAll("[data-campaign-unit]").forEach((element) => {
        const unit = element.dataset.campaignUnit;
        const value = values[unit];

        if (typeof value === "number") {
          element.textContent = String(value).padStart(2, "0");
        }
      });

      return true;
    }

    activate() {
      this.state = "live";
      this.dataset.campaignPhase = "live";
      const clock = this.querySelector("[data-campaign-clock]");
      if (clock) {
        clock.dateTime = this.dataset.campaignEnd;
      }

      this.hidden = false;
      this.removeAttribute("aria-hidden");
    }

    hide(state) {
      this.state = state;
      this.dataset.campaignPhase = state;
      this.hidden = true;
      this.setAttribute("aria-hidden", "true");
    }

    expire(state = "expired") {
      this.cleanup();
      this.hide(state);
    }

    cleanup() {
      this.stop();
      if (this.visibilityHandler) {
        document.removeEventListener("visibilitychange", this.visibilityHandler);
        this.visibilityHandler = null;
      }
    }

    stop() {
      if (this.intervalId !== undefined && this.intervalId !== null) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }

  customElements.define(elementName, GlamoraCampaignCountdown);
})();
